import { Card, CardContent, CardHeader } from "shared/ui/shadcn/card";
import { Badge } from "shared/ui/shadcn/badge";
import { clsxMerge } from "shared/lib/helpers";
import { Button } from "shared/ui/shadcn/button";
import { TrashIcon } from "lucide-react";
import { Divider } from "@/modules/email-templates-sidebar/shared/divider";
import { selectorDateToFollowupSetting } from "@/components/modules/email-templates-sidebar/selectors";
import { useMemo } from "react";
import { SCHEDULER_TIMEZONES } from "@/modules/email-templates-sidebar/content/v2/sections/preview-settings-section/scheduler/select-timezone";
import { DayJs } from "shared/lib/helpers/date";
import { DATE_FORMAT_MONTH_DATE_YEAR_SLASHED } from "shared/lib/constants/time";

interface EmailPreviewCardProps {
  index?: number;
  subject?: string;
  body?: string;
  scheduledAt?: string;
  prevScheduledAt?: string;
  isSelected?: boolean;
  isDisabled?: boolean;
  onClick?: () => void;
  onRemove?: () => void;
}

/**
 * Email preview card
 *
 * @param index - index of the email
 * @param subject - subject of the email
 * @param body - body of the email
 * @param scheduledAt - scheduled at of the email
 * @param prevScheduledAt - previous email schedule date as it imapcts days
 * @param isSelected - if the email is selected
 * @param isDisabled - if the email is disabled
 * @param onClick - onClick of the email
 * @param onRemove - onRemove of the email
 */
export const EmailPreviewCard = ({
  index,
  subject,
  body,
  scheduledAt,
  prevScheduledAt,
  isSelected = false,
  isDisabled,
  onClick,
  onRemove,
}: EmailPreviewCardProps) => {
  const handleRemove = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    onRemove?.();
  };

  const {
    days,
    time,
    timezone,
  }: { days?: string; time?: string; timezone?: string } = useMemo(() => {
    if (!scheduledAt) return {};

    const previewSettings = selectorDateToFollowupSetting(
      scheduledAt as string,
      prevScheduledAt
    );

    previewSettings.timezone = SCHEDULER_TIMEZONES.find(
      (tz) => tz.tzCode === previewSettings.timezone
    )?.abbr;

    return previewSettings;
  }, [scheduledAt, prevScheduledAt]);

  const formattedScheduledAt = useMemo(() => {
    if (!scheduledAt) return "";

    const date = DayJs(scheduledAt);

    return date.format(DATE_FORMAT_MONTH_DATE_YEAR_SLASHED);
  }, [scheduledAt]);

  return (
    <Card
      className={clsxMerge(
        "w-full animate-fadein transition-all duration-300",
        {
          "border-[#4474E3]": isSelected,
          "cursor-pointer hover:border-primary/50": !isSelected,
          "pointer-events-none cursor-not-allowed opacity-70": isDisabled,
        }
      )}
      onClick={onClick}
    >
      <CardHeader className="p-4 pb-2">
        <div className="flex min-h-[35px] items-center justify-between">
          <h4 className="typography-body-4-medium">
            {index === 0
              ? "Original email"
              : `Follow-up Email ${index} preview`}
          </h4>

          <div className="flex items-center gap-2">
            {isSelected && (
              <Badge className="animate-fadein bg-[#4474E3] text-white hover:bg-[#4474E3]">
                Selected
              </Badge>
            )}

            {onRemove && (
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full"
                onClick={handleRemove}
              >
                <TrashIcon className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {scheduledAt && (
          <span className="relative -top-1 typography-body-4">
            Will be sent{" "}
            <b className="font-semibold">
              {days} days ({formattedScheduledAt})
            </b>{" "}
            after the last email at{" "}
            <b className="font-semibold">
              {time} {timezone}
            </b>
          </span>
        )}

        <Divider />
      </CardHeader>
      <CardContent className="mx-4 mb-5 p-0 text-[#333]">
        <div className="space-y-2 rounded-lg bg-[#FBFBFB] px-2 pb-3 pt-1">
          <div>
            <p className="typography-body-4-medium">Subject</p>
            <p className="line-clamp-1 typography-body-4">
              {subject ? subject : "-"}
            </p>
          </div>
          <div>
            <p className="typography-body-4-medium">Body</p>
            <p className="line-clamp-3 typography-body-4">
              {body ? body : "-"}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
